import { z } from 'zod';
import { Role } from './role.enum';
import { isValidIndianMobileE164, normalizeIndianPhone } from './phone';

export const RegisterOrgDto = z.object({
  orgName: z.string().min(1).max(200),
  adminEmail: z.string().email(),
  adminPhone: z
    .string()
    .regex(/^\+91[6-9][0-9]{9}$/, 'Must be a valid Indian mobile: +91XXXXXXXXXX'),
  password: z.string().min(8).max(128),
});

export const LoginDto = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});

export const MfaVerifyDto = z.object({
  email: z.string().email(),
  code: z.string().length(6),
  sessionToken: z.string().min(1),
});

export const CreateInvitationDto = z.object({
  email: z.string().email(),
  role: z.nativeEnum(Role),
  organizationId: z.string().uuid(),
});

export const ResetPasswordDto = z.object({
  email: z.string().email(),
  newPassword: z.string().min(8).max(128),
  token: z.string().min(1),
});

export type RegisterOrgInput = z.infer<typeof RegisterOrgDto>;
export type LoginInput = z.infer<typeof LoginDto>;
export type MfaVerifyInput = z.infer<typeof MfaVerifyDto>;
export type CreateInvitationInput = z.infer<typeof CreateInvitationDto>;
export type ResetPasswordInput = z.infer<typeof ResetPasswordDto>;

export const PatientContactCreateDto = z.object({
  primaryPhone: z
    .string()
    .min(1, 'Phone number is required')
    .transform((v) => normalizeIndianPhone(v))
    .pipe(
      z
        .string()
        .refine(isValidIndianMobileE164, 'Must be a valid Indian mobile: +91XXXXXXXXXX'),
    ),
  alternatePhone: z
    .string()
    .max(15, 'Phone number must be 15 digits or less')
    .optional()
    .or(z.literal('')),
  email: z
    .string()
    .email('Please enter a valid email address')
    .optional()
    .or(z.literal('')),
  addressLine1: z.string().max(200, 'Address must be 200 characters or less').optional(),
  addressLine2: z.string().max(200, 'Address must be 200 characters or less').optional(),
  city: z.string().max(100, 'City must be 100 characters or less').optional(),
  state: z.string().max(100, 'State must be 100 characters or less').optional(),
  nationality: z.string().max(100, 'Nationality must be 100 characters or less').optional(),
  countryId: z.string().uuid().optional(),
  stateId: z.string().uuid().optional(),
  districtId: z.string().uuid().nullable().optional(),
  pinCode: z.string().max(10, 'Pin code must be 10 characters or less').optional(),
  emergencyContactName: z.string().max(200, 'Name must be 200 characters or less').optional(),
  emergencyContactRelation: z.string().max(100, 'Relation must be 100 characters or less').optional(),
  emergencyContactPhone: z
    .string()
    .max(15, 'Phone number must be 15 digits or less')
    .optional()
    .or(z.literal('')),
});

export const PatientContactUpdateDto = PatientContactCreateDto.partial();

const DateOfBirthSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be a valid date (YYYY-MM-DD)')
  .refine((val) => {
    const d = new Date(val);
    return !isNaN(d.getTime()) && d <= new Date();
  }, 'Date of birth cannot be in the future')
  .refine((val) => {
    const d = new Date(val);
    const ageYears = (Date.now() - d.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
    return ageYears >= 0 && ageYears <= 150;
  }, 'Please enter a valid date of birth');

const AgeSchema = z.number().int('Age must be a whole number').min(0, 'Age cannot be negative').max(150, 'Age must be 150 or less');

export const PatientCreateDto = z.object({
  firstName: z.string().min(1, 'First name is required').max(100, 'First name must be 100 characters or less'),
  lastName: z.string().min(1, 'Last name is required').max(100, 'Last name must be 100 characters or less'),
  dateOfBirth: DateOfBirthSchema.optional(),
  age: AgeSchema.optional(),
  gender: z.enum(['male', 'female', 'other', 'prefer_not_to_say'], {
    message: 'Please select a valid gender',
  }),
  bloodGroup: z.enum(['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-']).optional(),
  occupation: z.string().max(200, 'Occupation must be 200 characters or less').optional(),
  medicalNotes: z.string().max(2000, 'Medical notes must be 2000 characters or less').optional(),
  primaryDoctorId: z.string().uuid().nullable().optional(),
  contact: PatientContactCreateDto,
}).refine(
  (data) => data.dateOfBirth !== undefined || data.age !== undefined,
  { message: 'Please provide either Date of Birth or Age', path: ['dateOfBirth'] },
);

export const PatientUpdateDto = z.object({
  firstName: z.string().min(1, 'First name is required').max(100, 'First name must be 100 characters or less').optional(),
  lastName: z.string().min(1, 'Last name is required').max(100, 'Last name must be 100 characters or less').optional(),
  dateOfBirth: DateOfBirthSchema.optional().nullable(),
  age: AgeSchema.optional().nullable(),
  gender: z.enum(['male', 'female', 'other', 'prefer_not_to_say'], {
    message: 'Please select a valid gender',
  }).optional(),
  bloodGroup: z.enum(['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-']).nullable().optional(),
  occupation: z.string().max(200, 'Occupation must be 200 characters or less').nullable().optional(),
  medicalNotes: z.string().max(2000, 'Medical notes must be 2000 characters or less').nullable().optional(),
  primaryDoctorId: z.string().uuid().nullable().optional(),
  isActive: z.boolean().optional(),
  contact: PatientContactUpdateDto.optional(),
});

export const PatientContactResponseDto = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  clinicId: z.string().uuid(),
  patientId: z.string().uuid(),
  primaryPhone: z.string(),
  alternatePhone: z.string().nullable(),
  email: z.string().email().nullable(),
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  nationality: z.string().nullable(),
  countryId: z.string().uuid().nullable(),
  stateId: z.string().uuid().nullable(),
  districtId: z.string().uuid().nullable(),
  pinCode: z.string().nullable(),
  emergencyContactName: z.string().nullable(),
  emergencyContactRelation: z.string().nullable(),
  emergencyContactPhone: z.string().nullable(),
});

export const PatientResponseDto = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  clinicId: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string(),
  name: z.string(),
  dateOfBirth: z.string().nullable(),
  age: z.number().int().nullable(),
  gender: z.string(),
  bloodGroup: z.string().nullable(),
  occupation: z.string().nullable(),
  patientDisplayId: z.string(),
  isActive: z.boolean(),
  deletedAt: z.string().nullable().optional(),
  medicalNotes: z.string().nullable(),
  primaryDoctorId: z.string().uuid().nullable(),
  createdBy: z.string().nullable(),
  updatedBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  contacts: PatientContactResponseDto.nullable().optional(),
});

export type PatientCreateInput = z.infer<typeof PatientCreateDto>;
export type PatientUpdateInput = z.infer<typeof PatientUpdateDto>;
export type PatientContactCreateInput = z.infer<typeof PatientContactCreateDto>;
export type PatientContactUpdateInput = z.infer<typeof PatientContactUpdateDto>;
export type PatientResponse = z.infer<typeof PatientResponseDto>;
export type PatientContactResponse = z.infer<typeof PatientContactResponseDto>;
